import React, { useState, useEffect, useId, useRef } from "react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { createPortal } from "react-dom";
import { HiOutlineCalendar, HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi2";
import { Z_INDEX } from "@/utils/z-index";

// Configure dayjs plugins
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("fr");

interface DateRangePickerProps {
  dateFrom?: string | null;
  dateTo?: string | null;
  onChange: (dateFrom: string | null, dateTo: string | null) => void;
  className?: string;
  size?: "sm" | "md" | "lg";
}

// Size configurations
const sizeConfig = {
  sm: { inputClass: "py-1.5 text-sm", iconClass: "w-4 h-4" },
  md: { inputClass: "py-2.5 text-sm", iconClass: "w-5 h-5" },
  lg: { inputClass: "py-3.5 text-base", iconClass: "w-5 h-5" },
};

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

/**
 * DateRangePicker component for selecting date ranges in ISO format (YYYY-MM-DD)
 * Pure Tailwind implementation with custom calendar
 */
const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateFrom,
  dateTo,
  onChange,
  className = "",
  size = "md",
}) => {
  const [localFrom, setLocalFrom] = useState<Date | null>(null);
  const [localTo, setLocalTo] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openPicker, setOpenPicker] = useState<"from" | "to" | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  
  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const fromId = useId();
  const toId = useId();

  // Parse ISO string to Date
  const parseISODate = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr) return null;
    const parsed = dayjs(dateStr, "YYYY-MM-DD");
    return parsed.isValid() ? parsed.toDate() : null;
  };

  // Format Date to ISO string
  const formatToISO = (date: Date | null): string | null => {
    if (!date) return null;
    const d = dayjs(date);
    return d.isValid() ? d.format("YYYY-MM-DD") : null;
  };

  // Format Date for display
  const formatForDisplay = (date: Date | null): string => {
    if (!date) return "";
    const d = dayjs(date);
    return d.isValid() ? d.format("DD/MM/YYYY") : "";
  };

  // Sync with external values
  useEffect(() => {
    setLocalFrom(parseISODate(dateFrom));
    setLocalTo(parseISODate(dateTo));
  }, [dateFrom, dateTo]);

  // Update picker position
  useEffect(() => {
    if (openPicker) {
      const ref = openPicker === "from" ? fromRef : toRef;
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setPickerPosition({
          top: rect.bottom + 8,
          left: rect.left,
        });
      }
    }
  }, [openPicker]);

  // Close on click outside
  useEffect(() => {
    if (!openPicker) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node) &&
        fromRef.current &&
        !fromRef.current.contains(e.target as Node) &&
        toRef.current &&
        !toRef.current.contains(e.target as Node)
      ) {
        setOpenPicker(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openPicker]);

  const handleDateSelect = (date: Date) => {
    if (openPicker === "from") {
      setLocalFrom(date);
      if (localTo && dayjs(date).isAfter(dayjs(localTo))) {
        setError("La date de début doit être antérieure à la date de fin");
        return;
      }
      setError(null);
      onChange(formatToISO(date), formatToISO(localTo));
      setOpenPicker(null);
    } else if (openPicker === "to") {
      setLocalTo(date);
      if (localFrom && dayjs(date).isBefore(dayjs(localFrom))) {
        setError("La date de fin doit être postérieure à la date de début");
        return;
      }
      setError(null);
      onChange(formatToISO(localFrom), formatToISO(date));
      setOpenPicker(null);
    }
  };

  const handleOpenPicker = (type: "from" | "to") => {
    setOpenPicker(type);
    // Set current month to the selected date or today
    const selectedDate = type === "from" ? localFrom : localTo;
    setCurrentMonth(selectedDate || new Date());
  };

  const today = new Date();
  const { inputClass, iconClass } = sizeConfig[size];

  // Generate calendar days using dayjs
  const currentMonthDayjs = dayjs(currentMonth);
  const monthStart = currentMonthDayjs.startOf("month");
  const monthEnd = currentMonthDayjs.endOf("month");
  const daysInMonth = monthEnd.date();
  const days: Date[] = [];
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(monthStart.date(i).toDate());
  }

  // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
  // Adjust for Monday start (French calendar)
  const startDayOfWeek = monthStart.day();
  const paddingDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

  const isDateDisabled = (date: Date) => {
    const d = dayjs(date);
    if (openPicker === "from") {
      return localTo ? d.isAfter(dayjs(localTo)) : d.isAfter(dayjs(today));
    }
    if (openPicker === "to") {
      if (localFrom && d.isBefore(dayjs(localFrom))) return true;
      return d.isAfter(dayjs(today));
    }
    return false;
  };

  const isDateSelected = (date: Date) => {
    const d = dayjs(date);
    if (openPicker === "from") {
      return localFrom ? d.isSame(dayjs(localFrom), "day") : false;
    }
    if (openPicker === "to") {
      return localTo ? d.isSame(dayjs(localTo), "day") : false;
    }
    return false;
  };

  const isDateInRange = (date: Date) => {
    if (!localFrom || !localTo) return false;
    const d = dayjs(date);
    return d.isAfter(dayjs(localFrom)) && d.isBefore(dayjs(localTo));
  };

  const renderCalendar = () => {
    if (!openPicker || typeof document === "undefined") return null;

    return createPortal(
      <div
        ref={pickerRef}
        className="bg-surface-elevated rounded-xl shadow-xl border border-arc-border p-4 w-80 animate-fadeIn"
        style={{
          position: "fixed",
          top: pickerPosition.top,
          left: pickerPosition.left,
          zIndex: Z_INDEX.POPOVER,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => setCurrentMonth(dayjs(currentMonth).subtract(1, "month").toDate())}
            className="p-2 rounded-lg hover:bg-surface-secondary text-content-secondary hover:text-content-primary transition-colors"
          >
            <HiOutlineChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-base font-semibold text-content-primary capitalize">
            {dayjs(currentMonth).format("MMMM YYYY")}
          </span>
          <button
            type="button"
            onClick={() => setCurrentMonth(dayjs(currentMonth).add(1, "month").toDate())}
            className="p-2 rounded-lg hover:bg-surface-secondary text-content-secondary hover:text-content-primary transition-colors"
          >
            <HiOutlineChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAYS.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-semibold text-content-tertiary py-1"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Days */}
        <div className="grid grid-cols-7 gap-1">
          {/* Padding for days before the first of the month */}
          {Array.from({ length: paddingDays }).map((_, i) => (
            <div key={`padding-${i}`} className="h-9" />
          ))}

          {days.map((day) => {
            const disabled = isDateDisabled(day);
            const selected = isDateSelected(day);
            const inRange = isDateInRange(day);
            const isToday = dayjs(day).isSame(dayjs(today), "day");

            return (
              <button
                key={day.toISOString()}
                type="button"
                disabled={disabled}
                onClick={() => handleDateSelect(day)}
                className={`
                  h-9 w-full rounded-lg text-sm font-medium
                  transition-all duration-150
                  focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1
                  ${
                    selected
                      ? "bg-accent text-white shadow-sm"
                      : inRange
                      ? "bg-accent/10 text-accent"
                      : disabled
                      ? "text-content-tertiary cursor-not-allowed"
                      : "text-content-primary hover:bg-accent/10 hover:text-accent"
                  }
                  ${isToday && !selected ? "ring-2 ring-accent ring-inset" : ""}
                `}
              >
                {dayjs(day).date()}
              </button>
            );
          })}
        </div>

        {/* Quick actions */}
        <div className="mt-4 pt-3 border-t border-arc-border flex gap-2">
          <button
            type="button"
            onClick={() => handleDateSelect(today)}
            className="flex-1 py-1.5 text-xs font-medium text-accent hover:bg-accent/10 rounded-lg transition-colors"
          >
            Aujourd&apos;hui
          </button>
          <button
            type="button"
            onClick={() => {
              if (openPicker === "from") {
                setLocalFrom(null);
                onChange(null, formatToISO(localTo));
              } else {
                setLocalTo(null);
                onChange(formatToISO(localFrom), null);
              }
              setOpenPicker(null);
            }}
            className="flex-1 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-secondary rounded-lg transition-colors"
          >
            Effacer
          </button>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex gap-3">
        {/* From Date */}
        <div className="flex-1">
          <label
            htmlFor={fromId}
            className="mb-2 block text-sm font-semibold text-content-primary"
          >
            Du
          </label>
          <div
            ref={fromRef}
            onClick={() => handleOpenPicker("from")}
            className={`
              flex items-center gap-2 px-3 ${inputClass}
              bg-surface-elevated border-2 rounded-xl cursor-pointer
              transition-all duration-200
              ${
                openPicker === "from"
                  ? "border-accent"
                  : error
                  ? "border-error"
                  : "border-arc-border hover:border-arc-border-strong"
              }
            `}
          >
            <HiOutlineCalendar className={`${iconClass} text-content-tertiary`} />
            <input
              id={fromId}
              type="text"
              readOnly
              value={formatForDisplay(localFrom)}
              placeholder="JJ/MM/AAAA"
              className="flex-1 bg-transparent outline-none cursor-pointer text-content-primary placeholder:text-content-tertiary"
              aria-label="Date de début"
            />
          </div>
        </div>

        {/* To Date */}
        <div className="flex-1">
          <label
            htmlFor={toId}
            className="mb-2 block text-sm font-semibold text-content-primary"
          >
            Au
          </label>
          <div
            ref={toRef}
            onClick={() => handleOpenPicker("to")}
            className={`
              flex items-center gap-2 px-3 ${inputClass}
              bg-surface-elevated border-2 rounded-xl cursor-pointer
              transition-all duration-200
              ${
                openPicker === "to"
                  ? "border-accent"
                  : error
                  ? "border-error"
                  : "border-arc-border hover:border-arc-border-strong"
              }
            `}
          >
            <HiOutlineCalendar className={`${iconClass} text-content-tertiary`} />
            <input
              id={toId}
              type="text"
              readOnly
              value={formatForDisplay(localTo)}
              placeholder="JJ/MM/AAAA"
              className="flex-1 bg-transparent outline-none cursor-pointer text-content-primary placeholder:text-content-tertiary"
              aria-label="Date de fin"
            />
          </div>
        </div>
      </div>

      {error && (
        <p className="mt-1.5 text-xs text-error" role="alert">
          {error}
        </p>
      )}

      {renderCalendar()}
    </div>
  );
};

export default DateRangePicker;
